
import React, { useEffect, useState, useRef, useMemo } from "react";
import axiosInstance from "../../utils/axiosInstance";

/* ─── Styles ─────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;1,9..144,400&display=swap');

  :root {
    --bg: #f0fdfa;
    --surface: #ffffff;
    --surface-2: #f0fdfa;
    --surface-3: #ccfbf1;
    --border: #d1faf4;
    --border-strong: #99f6e4;
    --text-primary: #0f172a;
    --text-secondary: #475569;
    --text-muted: #94a3b8;
    --accent: #14b8a6;
    --accent-dark: #0d9488;
    --accent-dim: rgba(20,184,166,0.1);
    --full: #14b8a6;
    --full-bg: rgba(20,184,166,0.1);
    --half: #f59e0b;
    --half-bg: rgba(245,158,11,0.1);
    --night: #8b5cf6;
    --night-bg: rgba(139,92,246,0.1);
    --absent: #ef4444;
    --absent-bg: rgba(239,68,68,0.1);
    --radius: 12px;
    --radius-sm: 8px;
    --shadow: 0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.05);
  }

  .wa * { box-sizing: border-box; margin: 0; padding: 0; }

  .wa {
    // font-family: 'Plus Jakarta Sans', sans-serif;
    // background: var(--bg);
    min-height: 100vh;
    padding: 32px 28px 64px;
    color: var(--text-primary);
  }

  .wa-header {
    max-width: 1200px;
    margin: 0 auto 28px;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  .wa-header h1 {
    // font-family: 'Fraunces', serif;
    font-size: 2rem;
    font-weight: 600;
    letter-spacing: -0.3px;
    color: var(--accent-dark);
    line-height: 1.1;
  }
  .wa-header p { margin-top: 5px; font-size: 0.83rem; color: var(--text-muted); }
  .wa-count-badge {
    font-size: 0.78rem; font-weight: 600; color: var(--accent-dark);
    background: var(--accent-dim); border: 1px solid rgba(20,184,166,0.25);
    padding: 5px 13px; border-radius: 100px;
  }

  /* Toolbar */
  .wa-toolbar {
    max-width: 1200px; margin: 0 auto 16px;
    background: var(--surface); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 14px 18px;
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
    box-shadow: var(--shadow);
  }
  .wa-search-wrap { position: relative; flex: 1; min-width: 180px; }
  .wa-search-ico {
    position: absolute; left: 11px; top: 50%; transform: translateY(-50%);
    color: var(--text-muted); display: flex; pointer-events: none;
  }
  .wa-search {
    width: 100%; padding: 9px 12px 9px 34px;
    background: var(--surface-2); border: 1px solid var(--border);
    border-radius: var(--radius-sm); color: var(--text-primary);
    // font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.85rem;
    outline: none; transition: border-color 0.18s, box-shadow 0.18s;
  }
  .wa-search:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(20,184,166,0.15); }
  .wa-search::placeholder { color: var(--text-muted); }
  .wa-chips { display: flex; gap: 6px; flex-wrap: wrap; }
  .wa-chip {
    padding: 6px 13px; border-radius: 100px; border: 1px solid var(--border);
    background: var(--surface-2); color: var(--text-secondary);
    // font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.78rem; font-weight: 600;
    cursor: pointer; transition: all 0.16s; white-space: nowrap;
  }
  .wa-chip:hover { border-color: var(--border-strong); color: var(--text-primary); }
  .wa-chip.active { background: var(--accent); border-color: var(--accent); color: #fff; }
  .wa-sep { width: 1px; height: 28px; background: var(--border); flex-shrink: 0; }
  .wa-date-wrap { display: flex; align-items: center; gap: 8px; }
  .wa-date-lbl {
    font-size: 0.78rem; font-weight: 600; color: var(--text-muted);
    white-space: nowrap; letter-spacing: 0.04em; text-transform: uppercase;
  }
  .wa-date {
    padding: 8px 11px; background: var(--surface-2); border: 1px solid var(--border);
    border-radius: var(--radius-sm); color: var(--text-primary);
    // font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.84rem;
    outline: none; cursor: pointer; transition: border-color 0.16s; color-scheme: dark;
  }
  .wa-date:focus { border-color: var(--accent); }
  .wa-save {
    padding: 9px 22px; background: var(--accent); color: #fff; border: none;
    border-radius: var(--radius-sm); font-family: 'Plus Jakarta Sans', sans-serif;
    // font-size: 0.85rem; font-weight: 700; cursor: pointer;
    transition: background 0.16s, transform 0.12s; white-space: nowrap;
    display: flex; align-items: center; gap: 7px;
  }
  .wa-save:hover:not(:disabled) { background: var(--accent-dark); }
  .wa-save:active:not(:disabled) { transform: scale(0.97); }
  .wa-save:disabled { opacity: 0.35; cursor: not-allowed; }

  /* Stats */
  .wa-stats {
    max-width: 1200px; margin: 0 auto 16px;
    display: flex; gap: 10px; flex-wrap: wrap;
  }
  .wa-stat {
    display: flex; align-items: center; gap: 8px;
    background: var(--surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); padding: 10px 16px; font-size: 0.82rem;
  }
  .wa-stat-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .wa-stat-dot.full   { background: var(--full); }
  .wa-stat-dot.half   { background: var(--half); }
  .wa-stat-dot.night  { background: var(--night); }
  .wa-stat-dot.absent { background: var(--absent); }
  .wa-stat-num { font-weight: 700; font-size: 1rem; color: var(--text-primary); }
  .wa-stat-txt { color: var(--text-secondary); }

  /* Table */
  .wa-table-wrap {
    max-width: 1200px; margin: 0 auto;
    background: var(--surface); border: 1px solid var(--border);
    border-radius: var(--radius); box-shadow: var(--shadow);
    overflow: hidden; overflow-x: auto;
  }
  .wa-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; min-width: 760px; }
  .wa-table thead { background: var(--surface-2); border-bottom: 1px solid var(--border-strong); }
  .wa-table thead th {
    padding: 13px 16px; text-align: left;
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--text-muted); white-space: nowrap;
  }
  .wa-table thead th:first-child { padding-left: 22px; }
  .wa-table thead th:last-child  { padding-right: 22px; text-align: right; }
  .wa-table tbody tr {
    border-bottom: 1px solid var(--border);
    transition: background 0.13s;
  }
  .wa-table tbody tr:last-child { border-bottom: none; }
  .wa-table tbody tr:hover { background: var(--surface-2); }
  .wa-table tbody tr.marked-full   { border-left: 2.5px solid var(--full); }
  .wa-table tbody tr.marked-half   { border-left: 2.5px solid var(--half); }
  .wa-table tbody tr.marked-night  { border-left: 2.5px solid var(--night); }
  .wa-table tbody tr.marked-absent { border-left: 2.5px solid var(--absent); }
  .wa-table td { padding: 13px 16px; color: var(--text-primary); vertical-align: middle; }
  .wa-table td:first-child { padding-left: 22px; }
  .wa-table td:last-child  { padding-right: 22px; }

  /* Cells */
  .wa-cell-worker { display: flex; align-items: center; gap: 11px; }
  .wa-avatar {
    width: 34px; height: 34px; border-radius: 8px;
    background: var(--accent-dim); color: var(--accent-dark);
    // font-family: 'Fraunces', serif; font-size: 0.9rem;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .wa-w-name { font-weight: 600; font-size: 0.88rem; }
  .wa-w-sub  { font-size: 0.74rem; color: var(--text-muted); margin-top: 1px; }
  .wa-tag {
    display: inline-block; padding: 3px 9px; border-radius: 5px;
    font-size: 0.72rem; font-weight: 600;
    background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0;
  }
  .wa-tag.site { background: var(--accent-dim); color: var(--accent-dark); border-color: rgba(20,184,166,0.25); }

  .wa-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 11px; border-radius: 100px;
    font-size: 0.7rem; font-weight: 700;
    letter-spacing: 0.04em; text-transform: uppercase; white-space: nowrap;
  }
  .wa-badge::before { content:''; width:5px; height:5px; border-radius:50%; background:currentColor; display:block; }
  .wa-badge.full    { background: var(--full-bg);   color: var(--full);   }
  .wa-badge.half    { background: var(--half-bg);   color: var(--half);   }
  .wa-badge.night   { background: var(--night-bg);  color: var(--night);  }
  .wa-badge.absent  { background: var(--absent-bg); color: var(--absent); }
  .wa-badge.unmarked{ background: var(--surface-3); color: var(--text-muted); }

  .wa-att-btns { display: flex; gap: 5px; flex-wrap: nowrap; }
  .wa-att-btn {
    padding: 6px 12px; border-radius: 6px;
    border: 1px solid var(--border); background: var(--surface-2);
    color: var(--text-secondary);
    // font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.74rem; font-weight: 600;
    cursor: pointer; transition: all 0.14s; white-space: nowrap;
  }
  .wa-att-btn:hover { border-color: var(--border-strong); color: var(--text-primary); }
  .wa-att-btn.full.active   { background: var(--full-bg);   border-color: var(--full);   color: var(--full);   }
  .wa-att-btn.half.active   { background: var(--half-bg);   border-color: var(--half);   color: var(--half);   }
  .wa-att-btn.night.active  { background: var(--night-bg);  border-color: var(--night);  color: var(--night);  }
  .wa-att-btn.absent.active { background: var(--absent-bg); border-color: var(--absent); color: var(--absent); }
  .wa-att-btn.full:hover    { background: var(--full-bg);   border-color: var(--full);   color: var(--full);   }
  .wa-att-btn.half:hover    { background: var(--half-bg);   border-color: var(--half);   color: var(--half);   }
  .wa-att-btn.night:hover   { background: var(--night-bg);  border-color: var(--night);  color: var(--night);  }
  .wa-att-btn.absent:hover  { background: var(--absent-bg); border-color: var(--absent); color: var(--absent); }

  .wa-icon-btn {
    width: 30px; height: 30px; border-radius: 6px;
    border: 1px solid var(--border); background: var(--surface-2);
    color: var(--text-muted); cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.14s; flex-shrink: 0;
  }
  .wa-icon-btn:hover { border-color: var(--border-strong); color: var(--text-primary); background: var(--surface-3); }

  .wa-table-footer {
    padding: 11px 22px; background: var(--surface-2); border-top: 1px solid var(--border);
    font-size: 0.76rem; color: var(--text-muted);
    display: flex; align-items: center; justify-content: space-between;
  }

  .wa-empty { padding: 60px 24px; text-align: center; }
  .wa-empty-ico { font-size: 1.8rem; margin-bottom: 8px; }
  .wa-empty h3 { 
  // font-family:'Fraunces',serif; 
  font-size:1rem; color:var(--text-secondary); margin-bottom:4px; }
  .wa-empty p  { font-size:0.8rem; color:var(--text-muted); }

  .wa-loader { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:220px; gap:12px; }
  .wa-spinner {
    width:24px; height:24px; border:2.5px solid var(--border-strong);
    border-top-color:var(--accent); border-radius:50%;
    animation:spin 0.65s linear infinite;
  }
  @keyframes spin { to { transform:rotate(360deg); } }
  .wa-loader p { font-size:0.83rem; color:var(--text-muted); }

  /* Modal */
  .wa-backdrop {
    position:fixed; inset:0; background:rgba(15,23,42,0.4);
    backdrop-filter:blur(5px); z-index:200;
    display:flex; align-items:center; justify-content:center; padding:24px;
    animation:fadeIn 0.18s ease;
  }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  .wa-modal {
    background:var(--surface); border:1px solid var(--border-strong);
    border-radius:16px; width:100%; max-width:500px; max-height:76vh;
    display:flex; flex-direction:column;
    box-shadow:0 20px 60px rgba(15,23,42,0.15);
    animation:slideUp 0.22s ease;
  }
  @keyframes slideUp { from{transform:translateY(14px);opacity:0} to{transform:none;opacity:1} }
  .wa-modal-head {
    padding:22px 24px 18px; border-bottom:1px solid var(--border);
    display:flex; align-items:flex-start; justify-content:space-between; gap:12px;
  }
  .wa-modal-title { 
  // font-family:'Fraunces',serif; 
  font-size:1.2rem; font-weight:600; color:var(--text-primary); }
  .wa-modal-sub   { font-size:0.79rem; color:var(--text-muted); margin-top:3px; }
  .wa-modal-close {
    width:30px; height:30px; border-radius:7px; border:1px solid var(--border);
    background:var(--surface-2); color:var(--text-muted); cursor:pointer;
    display:flex; align-items:center; justify-content:center;
    font-size:0.9rem; transition:all 0.15s; flex-shrink:0;
  }
  .wa-modal-close:hover { background:var(--surface-3); color:var(--text-primary); }
  .wa-modal-summary {
    display:flex; gap:20px; flex-wrap:wrap; padding:13px 24px;
    background:var(--surface-2); border-bottom:1px solid var(--border);
    font-size:0.79rem; color:var(--text-secondary);
  }
  .wa-modal-summary strong { color:var(--text-primary); font-weight:600; }
  .wa-modal-body { overflow-y:auto; flex:1; }
  .wa-history-table { width:100%; border-collapse:collapse; font-size:0.84rem; }
  .wa-history-table thead th {
    padding:10px 24px; text-align:left;
    font-size:0.69rem; font-weight:700; letter-spacing:0.08em; text-transform:uppercase;
    color:var(--text-muted); background:var(--surface-2); border-bottom:1px solid var(--border);
  }
  .wa-history-table thead th:last-child { text-align:right; }
  .wa-history-table tbody tr { border-bottom:1px solid var(--border); transition:background 0.12s; }
  .wa-history-table tbody tr:last-child { border-bottom:none; }
  .wa-history-table tbody tr:hover { background:var(--surface-2); }
  .wa-history-table td { padding:11px 24px; color:var(--text-primary); }
  .wa-history-table td:last-child { text-align:right; }
  .wa-modal-foot { padding:14px 24px; border-top:1px solid var(--border); display:flex; justify-content:flex-end; }
  .wa-btn-sec {
    padding:8px 18px; border-radius:var(--radius-sm); border:1px solid var(--border);
    background:var(--surface-2); color:var(--text-secondary);
    // font-family:'Plus Jakarta Sans',sans-serif;
     font-size:0.83rem; font-weight:600;
    cursor:pointer; transition:all 0.15s;
  }
  .wa-btn-sec:hover { background:var(--surface-3); color:var(--text-primary); }

  @media (max-width:768px) {
    .wa { padding:18px 14px 48px; }
    .wa-toolbar { flex-direction:column; align-items:stretch; }
    .wa-save { justify-content:center; }
    .wa-sep { display:none; }
  }
`;

const ATTENDANCE_TYPES = { FULL_DAY:"FULL_DAY", HALF_DAY:"HALF_DAY", NIGHT_SHIFT:"NIGHT_SHIFT", ABSENT:"ABSENT", UNMARKED:null };
const FILTER_OPTIONS = [
  { key:"ALL", label:"All" }, { key:"UNMARKED", label:"Unmarked" },
  { key:"FULL_DAY", label:"Full Day" }, { key:"HALF_DAY", label:"Half Day" },
  { key:"NIGHT_SHIFT", label:"Night" }, { key:"ABSENT", label:"Absent" },
];
const BTNS = [
  { type:"FULL_DAY",    label:"Full Day", cls:"full"   },
  { type:"HALF_DAY",   label:"Half Day", cls:"half"   },
  { type:"NIGHT_SHIFT",label:"Night",    cls:"night"  },
  { type:"ABSENT",     label:"Absent",   cls:"absent" },
];
const statusCls   = (s) => ({ FULL_DAY:"full", HALF_DAY:"half", NIGHT_SHIFT:"night", ABSENT:"absent" }[s] || "");
const statusLabel = (s) => ({ FULL_DAY:"Full Day", HALF_DAY:"Half Day", NIGHT_SHIFT:"Night Shift", ABSENT:"Absent" }[s] || "Unmarked");
const initials    = (n="") => n.split(" ").slice(0,2).map(x=>x[0]).join("").toUpperCase();

export default function WorkerAttendance() {
  const [workers, setWorkers]               = useState([]);
  const [attendanceMap, setAttendanceMap]   = useState({});
  const [selectedDate, setSelectedDate]     = useState(()=>new Date().toISOString().split("T")[0]);
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [error, setError]                   = useState(null);
  const [search, setSearch]                 = useState("");
  const [filterStatus, setFilterStatus]     = useState("ALL");
  const [viewWorker, setViewWorker]         = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const res  = await axiosInstance.get("/workers/all");
        const data = Array.isArray(res.data) ? res.data : [];
        setWorkers(data);
        const m = {}; data.forEach((w) => { m[w.id] = null; }); setAttendanceMap(m);
      } catch (err) { setError(err.response?.data?.message || "Failed to load workers"); }
      finally { setLoading(false); }
    })();
  }, []);

  const filteredWorkers = useMemo(() => workers.filter((w) => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      w.workerName?.toLowerCase().includes(q) ||
      w.siteName?.toLowerCase().includes(q) ||
      w.workerType?.toLowerCase().includes(q);
    const s = attendanceMap[w.id];
    const matchFilter = filterStatus==="ALL" || (filterStatus==="UNMARKED" ? !s : s===filterStatus);
    return matchSearch && matchFilter;
  }), [workers, search, filterStatus, attendanceMap]);

  const stats = useMemo(() => ({
    full:   Object.values(attendanceMap).filter(v=>v==="FULL_DAY").length,
    half:   Object.values(attendanceMap).filter(v=>v==="HALF_DAY").length,
    night:  Object.values(attendanceMap).filter(v=>v==="NIGHT_SHIFT").length,
    absent: Object.values(attendanceMap).filter(v=>v==="ABSENT").length,
  }), [attendanceMap]);

  const anyMarked = Object.values(attendanceMap).some(Boolean);
  const toggleAttendance = (id, type) =>
    setAttendanceMap(p => ({ ...p, [id]: p[id]===type ? null : type }));

  const handleSave = async () => {
    const payload = Object.entries(attendanceMap)
      .filter(([,v])=>v)
      .map(([workerId,attendanceType])=>({ workerId:parseInt(workerId), date:selectedDate, attendanceType }));
    if (!payload.length) { alert("No attendance marked."); return; }
    setSaving(true);
    try {
      await axiosInstance.post("/workers/attendance/bulk", payload);
      alert("Attendance saved!");
      const r = {...attendanceMap}; payload.forEach(({workerId})=>{r[workerId]=null;}); setAttendanceMap(r);
    } catch(err) { alert(err.response?.data?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const openHistory = async (worker) => {
    setViewWorker(worker); setHistoryLoading(true);
    try {
      const res  = await axiosInstance.get(`/workers/${worker.id}/attendanceHistory`);
      const data = Array.isArray(res.data) ? res.data : [res.data];
      setAttendanceHistory(data.sort((a,b)=>new Date(b.date)-new Date(a.date)));
    } catch(err) { alert("Failed: "+(err.response?.data?.message||err.message)); setAttendanceHistory([]); }
    finally { setHistoryLoading(false); }
  };
  const closeModal = () => { setViewWorker(null); setAttendanceHistory([]); };

  if (loading) return <div className="wa"><style>{css}</style><div className="wa-loader"><div className="wa-spinner"/><p>Loading workers…</p></div></div>;
  if (error)   return <div className="wa"><style>{css}</style><div style={{textAlign:"center",color:"var(--absent)",padding:"80px 24px"}}>⚠ {error}</div></div>;

  return (
    <div className="wa">
      <style>{css}</style>

      {/* Header */}
      <div className="wa-header">
        <div><h1>Worker Attendance</h1><p>Mark and manage daily attendance records</p></div>
        <span className="wa-count-badge">{workers.length} Workers</span>
      </div>

      {/* Toolbar */}
      <div className="wa-toolbar">
        <div className="wa-search-wrap">
          <span className="wa-search-ico">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
          </span>
          <input className="wa-search" placeholder="Search name, site, type…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <div className="wa-chips">
          {FILTER_OPTIONS.map(f=>(
            <button key={f.key} className={`wa-chip ${filterStatus===f.key?"active":""}`} onClick={()=>setFilterStatus(f.key)}>{f.label}</button>
          ))}
        </div>
        <div className="wa-sep"/>
        <div className="wa-date-wrap">
          <span className="wa-date-lbl">Date</span>
          <input type="date" className="wa-date" value={selectedDate} onChange={e=>setSelectedDate(e.target.value)}/>
        </div>
        <button className="wa-save" onClick={handleSave} disabled={saving||!anyMarked}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
          </svg>
          {saving?"Saving…":"Save Attendance"}
        </button>
      </div>

      {/* Stats */}
      {anyMarked && (
        <div className="wa-stats">
          {[{cls:"full",label:"Full Day",count:stats.full},{cls:"half",label:"Half Day",count:stats.half},{cls:"night",label:"Night Shift",count:stats.night},{cls:"absent",label:"Absent",count:stats.absent}]
            .filter(s=>s.count>0).map(s=>(
            <div className="wa-stat" key={s.cls}>
              <div className={`wa-stat-dot ${s.cls}`}/>
              <span className="wa-stat-num">{s.count}</span>
              <span className="wa-stat-txt">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="wa-table-wrap">
        <table className="wa-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Worker</th>
              <th>Type</th>
              <th>Site</th>
              <th>Status</th>
              <th>Mark Attendance</th>
              <th style={{textAlign:"right"}}>History</th>
            </tr>
          </thead>
          <tbody>
            {filteredWorkers.length===0 ? (
              <tr><td colSpan={7}>
                <div className="wa-empty">
                  <div className="wa-empty-ico">🔍</div>
                  <h3>No workers found</h3>
                  <p>Try adjusting search or filter.</p>
                </div>
              </td></tr>
            ) : filteredWorkers.map((w,idx)=>{
              const s   = attendanceMap[w.id];
              const cls = statusCls(s);
              return (
                <tr key={w.id} className={cls?`marked-${cls}`:""}>
                  <td style={{color:"var(--text-muted)",fontSize:"0.78rem",fontWeight:500,width:44}}>{idx+1}</td>
                  <td>
                    <div className="wa-cell-worker">
                      <div className="wa-avatar">{initials(w.workerName)}</div>
                      <div>
                        <div className="wa-w-name">{w.workerName}</div>
                        {w.gender && <div className="wa-w-sub">{w.gender}</div>}
                      </div>
                    </div>
                  </td>
                  <td>{w.workerType ? <span className="wa-tag">{w.workerType}</span> : <span style={{color:"var(--text-muted)"}}>—</span>}</td>
                  <td>{w.siteName  ? <span className="wa-tag site">{w.siteName}</span>  : <span style={{color:"var(--text-muted)"}}>—</span>}</td>
                  <td><span className={`wa-badge ${cls||"unmarked"}`}>{statusLabel(s)}</span></td>
                  <td>
                    <div className="wa-att-btns">
                      {BTNS.map(({type,label,cls:bc})=>(
                        <button key={type} className={`wa-att-btn ${bc} ${s===type?"active":""}`} onClick={()=>toggleAttendance(w.id,type)}>{label}</button>
                      ))}
                    </div>
                  </td>
                  <td style={{textAlign:"right"}}>
                    <button className="wa-icon-btn" title="View History" onClick={()=>openHistory(w)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredWorkers.length>0 && (
          <div className="wa-table-footer">
            <span>Showing {filteredWorkers.length} of {workers.length} workers</span>
            <span>{anyMarked ? `${Object.values(attendanceMap).filter(Boolean).length} marked` : "None marked yet"}</span>
          </div>
        )}
      </div>

      {/* History Modal */}
      {viewWorker && (
        <AttendanceHistoryModal worker={viewWorker} history={attendanceHistory} loading={historyLoading} onClose={closeModal}/>
      )}
    </div>
  );
}

function AttendanceHistoryModal({ worker, history, loading, onClose }) {
  const modalRef = useRef(null);
  useEffect(() => {
    const h = e => { if (modalRef.current && !modalRef.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);

  const daysWorked = history.filter(r=>r.attendanceType!=="ABSENT").length;

  return (
    <div className="wa-backdrop">
      <div className="wa-modal" ref={modalRef}>
        <div className="wa-modal-head">
          <div>
            <div className="wa-modal-title">{worker.workerName}</div>
            <div className="wa-modal-sub">Attendance History</div>
          </div>
          <button className="wa-modal-close" onClick={onClose}>✕</button>
        </div>
        {history.length>0 && (
          <div className="wa-modal-summary">
            <span><strong>Site:</strong> {history[0]?.siteName||"—"}</span>
            <span><strong>Days Worked:</strong> {daysWorked}</span>
            <span><strong>Records:</strong> {history.length}</span>
          </div>
        )}
        <div className="wa-modal-body">
          {loading ? (
            <div className="wa-loader"><div className="wa-spinner"/><p>Loading…</p></div>
          ) : history.length===0 ? (
            <div className="wa-empty"><p>No records found.</p></div>
          ) : (
            <table className="wa-history-table">
              <thead><tr><th>#</th><th>Date</th><th>Status</th></tr></thead>
              <tbody>
                {history.map((rec,i)=>{
                  const cls = statusCls(rec.attendanceType);
                  return (
                    <tr key={rec.id||rec.date}>
                      <td style={{color:"var(--text-muted)",fontSize:"0.76rem",width:36}}>{i+1}</td>
                      <td style={{fontWeight:500}}>
                        {new Date(rec.date).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}
                      </td>
                      <td><span className={`wa-badge ${cls||"unmarked"}`}>{statusLabel(rec.attendanceType)}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="wa-modal-foot">
          <button className="wa-btn-sec" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}